/**
 * 元气大屏 — 中台通知 WebSocket（/ws/notify）
 *
 * 连接目标：门店角色房间 STORE_{storeId}_ROLE_{roleCode}（默认 yuanqi），
 * 用于接收治疗师呼叫等 staff_message_received 下行。
 *
 * 连接参数约定（与中台 NotifyRoomHandshakeInterceptor 对齐）：
 * - 必填：storeId、roleCode
 * - 元气大屏匿名模式（VUE_APP_WS_SKIP_TOKEN=true 且 roleCode=yuanqi）：
 *   可不传 staffUid、token，仅订阅角色房间（须后端支持 yuanqi 匿名握手）
 * - 其它角色或需鉴权时：须 staffUid + token（或登录态 token）
 *
 * 浏览器 WebSocket 无法自定义 Authorization，token 走 query。
 */

import { getToken, getNotifyStaffUid } from '@/utils/auth'
import { expectedYuanqiRoomKey } from '@/utils/roomKeys'
import logger from '@/utils/logger'
import screenLifecycleStats from '@/utils/screenLifecycleStats'

let socket = null
let reconnectTimer = null
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 10
const RECONNECT_INTERVAL_MS = 3000

/** 为 true 时 URL 不携带 token（元气大屏匿名订阅，须配合后端 yuanqi 无 token 握手） */
const SKIP_WS_TOKEN = String(process.env.VUE_APP_WS_SKIP_TOKEN || '').trim() === 'true'

const CLIENT_ROLE = String(process.env.VUE_APP_CLIENT_ROLE || 'yuanqi').trim().toLowerCase()

/** @type {Set<(msg: object) => void>} */
const messageListeners = new Set()

/** @type {Set<(evt: NotifyWsStatusEvent) => void>} */
const statusListeners = new Set()

/** 主动 disconnect 时置 true，避免 onclose 误报断开/重连 */
let manualDisconnect = false

function getWsBaseUrl() {
  const explicit = (process.env.VUE_APP_WS_BASE_URL || '').trim()
  if (explicit) return explicit.replace(/\/$/, '')
  const api = (process.env.VUE_APP_API_BASE || '').trim()
  if (!api) return ''
  return api.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:').replace(/\/$/, '')
}

/**
 * 是否为「元气大屏 kiosk」连接模式：只订阅门店角色房，不强制 staffUid / token。
 * 条件：角色为 yuanqi 且开启 VUE_APP_WS_SKIP_TOKEN。
 */
function isYuanqiKioskMode(roleCode = CLIENT_ROLE) {
  return roleCode === 'yuanqi' && SKIP_WS_TOKEN
}

/**
 * 校验能否构建 WS URL，并返回不可用原因（供大屏系统日志展示）。
 *
 * @param {string|number|null|undefined} storeId
 * @returns {{ ready: boolean, reason?: string }}
 */
export function getNotifyWsReadiness(storeId) {
  const base = getWsBaseUrl()
  const store = String(storeId || '').trim()
  const roleCode = CLIENT_ROLE
  const staffUid = getNotifyStaffUid()
  const token = SKIP_WS_TOKEN
    ? ''
    : String(getToken() || process.env.VUE_APP_NOTIFY_TOKEN || '').trim()
  const kiosk = isYuanqiKioskMode(roleCode)

  if (!base) return { ready: false, reason: 'MISSING_WS_BASE' }
  if (!store) return { ready: false, reason: 'MISSING_STORE_ID' }
  if (!roleCode) return { ready: false, reason: 'MISSING_ROLE_CODE' }
  if (!kiosk && !staffUid) return { ready: false, reason: 'MISSING_STAFF_UID' }
  if (!SKIP_WS_TOKEN && !token) return { ready: false, reason: 'MISSING_TOKEN' }

  return { ready: true }
}

/**
 * 构建 /ws/notify 完整 URL；条件不满足时返回 null。
 *
 * @param {string|number|null|undefined} storeId
 * @returns {string|null}
 */
export function buildNotifyWsUrl(storeId) {
  const { ready } = getNotifyWsReadiness(storeId)
  if (!ready) return null

  const base = getWsBaseUrl()
  const staffUid = getNotifyStaffUid()
  const store = String(storeId || '').trim()
  const roleCode = CLIENT_ROLE
  const token = SKIP_WS_TOKEN
    ? ''
    : String(getToken() || process.env.VUE_APP_NOTIFY_TOKEN || '').trim()
  const kiosk = isYuanqiKioskMode(roleCode)

  // staffUid：kiosk 模式可选（有登录则带上）；token：kiosk 不传，其它模式由 readiness 保证必有
  const q = [
    ...(staffUid ? [`staffUid=${encodeURIComponent(staffUid)}`] : []),
    `storeId=${encodeURIComponent(store)}`,
    `roleCode=${encodeURIComponent(roleCode)}`,
    ...(kiosk || !token ? [] : [`token=${encodeURIComponent(token)}`])
  ].join('&')

  return `${base}/ws/notify?${q}`
}

/** @deprecated 请使用 getNotifyWsReadiness；保留兼容 */
export function isNotifyWsReady(storeId) {
  return getNotifyWsReadiness(storeId).ready
}

/**
 * 将 getNotifyWsReadiness.reason 转为大屏可读中文提示。
 * @param {string|undefined} reason
 * @returns {string}
 */
export function formatNotifyWsNotReadyMessage(reason) {
  const map = {
    MISSING_WS_BASE: '未配置 WS 地址（VUE_APP_WS_BASE_URL 或 VUE_APP_API_BASE）',
    MISSING_STORE_ID: '未配置门店（URL 加 ?store=门店ID，或登录后选择门店）',
    MISSING_ROLE_CODE: '未配置角色（VUE_APP_CLIENT_ROLE）',
    MISSING_STAFF_UID: '缺少 staffUid（请登录、配置 VUE_APP_NOTIFY_STAFF_UID，或开启元气大屏匿名模式）',
    MISSING_TOKEN: '缺少 token（请登录或配置 VUE_APP_NOTIFY_TOKEN；元气大屏可设 VUE_APP_WS_SKIP_TOKEN=true）'
  }
  return map[reason] || `连接条件不满足 (${reason || 'unknown'})`
}

/**
 * @param {(msg: object) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onNotifyWsMessage(fn) {
  messageListeners.add(fn)
  return () => messageListeners.delete(fn)
}

/**
 * 订阅连接状态（open / close / error / reconnecting / reconnect_failed）。
 * @param {(evt: NotifyWsStatusEvent) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onNotifyWsStatus(fn) {
  statusListeners.add(fn)
  return () => statusListeners.delete(fn)
}

/**
 * @typedef {Object} NotifyWsStatusEvent
 * @property {'open'|'close'|'error'|'reconnecting'|'reconnect_failed'} type
 * @property {string|number} [storeId]
 * @property {string} [url]
 * @property {string} [room]
 * @property {string} [mode]
 * @property {number} [code]
 * @property {string} [reason]
 * @property {boolean} [manual]
 * @property {boolean} [willReconnect]
 * @property {number} [attempt]
 * @property {number} [maxAttempts]
 */

function dispatchStatus(evt) {
  statusListeners.forEach((fn) => {
    try { fn(evt) } catch (e) { logger.error('[notifyWs] status listener error', e) }
  })
}

function dispatchMessage(msg) {
  messageListeners.forEach((fn) => {
    try { fn(msg) } catch (e) { logger.error('[notifyWs] listener error', e) }
  })
}

function buildStatusContext(storeId, url) {
  const room = expectedYuanqiRoomKey(storeId)
  const mode = isYuanqiKioskMode() ? 'kiosk' : 'auth'
  return { storeId, url, room, mode }
}

/** 关键连接生命周期始终输出到浏览器 Console（不依赖 VUE_APP_DEBUG） */
function logNotifyWsLifecycle(message, detail) {
  if (detail !== undefined) {
    console.log(`[notifyWs] ${message}`, detail)
  } else {
    console.log(`[notifyWs] ${message}`)
  }
}

/**
 * 建立或复用通知 WebSocket。握手成功前仅表示已发起连接（见 onopen 日志）。
 *
 * @param {string|number|null|undefined} storeId
 * @returns {{ ok: boolean, reason?: string, url?: string, reused?: boolean }}
 */
export function syncNotifyWs(storeId) {
  const readiness = getNotifyWsReadiness(storeId)
  if (!readiness.ready) {
    disconnectNotifyWs()
    return { ok: false, reason: readiness.reason }
  }

  const url = buildNotifyWsUrl(storeId)
  if (!url) {
    disconnectNotifyWs()
    return { ok: false, reason: 'BUILD_URL_FAILED' }
  }

  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    if (socket.url === url) {
      const connected = socket.readyState === WebSocket.OPEN
      screenLifecycleStats.recordWsConnect({ reused: true, url })
      return { ok: true, reused: true, url, connected }
    }
  }

  disconnectNotifyWs({ resetAttempts: false })
  manualDisconnect = false
  socket = new WebSocket(url)
  screenLifecycleStats.recordWsConnect({ reused: false, url })

  socket.onopen = () => {
    reconnectAttempts = 0
    const ctx = buildStatusContext(storeId, url)
    logNotifyWsLifecycle('连接成功', {
      room: ctx.room,
      mode: ctx.mode,
      storeId: ctx.storeId,
      url: ctx.url
    })
    dispatchStatus({ type: 'open', ...ctx })
  }

  socket.onmessage = (evt) => {
    try {
      const msg = JSON.parse(evt.data)
      dispatchMessage(msg)
    } catch (e) {
      logger.warn('[notifyWs] parse message failed', e)
    }
  }

  socket.onclose = (evt) => {
    const ctx = buildStatusContext(storeId, url)
    const isManual = manualDisconnect || evt.code === 1000
    if (isManual) {
      logger.info('[notifyWs] closed (manual)', evt.code, evt.reason || '')
      dispatchStatus({
        type: 'close',
        ...ctx,
        code: evt.code,
        reason: evt.reason || '',
        manual: true,
        willReconnect: false
      })
      manualDisconnect = false
      reconnectAttempts = 0
      return
    }
    const canReconnect = isNotifyWsReady(storeId) && reconnectAttempts < MAX_RECONNECT_ATTEMPTS
    logger.warn('[notifyWs] closed', evt.code, evt.reason || '', canReconnect ? 'will reconnect' : 'no reconnect')
    dispatchStatus({
      type: 'close',
      ...ctx,
      code: evt.code,
      reason: evt.reason || '',
      manual: false,
      willReconnect: canReconnect
    })
    if (!isNotifyWsReady(storeId)) {
      reconnectAttempts = 0
      return
    }
    scheduleReconnect(storeId)
  }

  socket.onerror = () => {
    const ctx = buildStatusContext(storeId, url)
    logger.error('[notifyWs] error', url)
    dispatchStatus({ type: 'error', ...ctx })
  }

  return { ok: true, url }
}

function scheduleReconnect(storeId) {
  if (reconnectTimer) return
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    const url = buildNotifyWsUrl(storeId)
    const ctx = buildStatusContext(storeId, url || '')
    logger.error('[notifyWs] reconnect failed, max attempts', MAX_RECONNECT_ATTEMPTS)
    screenLifecycleStats.recordWsReconnectFailed({
      attempt: reconnectAttempts,
      maxAttempts: MAX_RECONNECT_ATTEMPTS,
    })
    dispatchStatus({
      type: 'reconnect_failed',
      ...ctx,
      attempt: reconnectAttempts,
      maxAttempts: MAX_RECONNECT_ATTEMPTS
    })
    return
  }
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    reconnectAttempts += 1
    const url = buildNotifyWsUrl(storeId)
    const ctx = buildStatusContext(storeId, url || '')
    logger.info('[notifyWs] reconnecting', reconnectAttempts, '/', MAX_RECONNECT_ATTEMPTS)
    dispatchStatus({
      type: 'reconnecting',
      ...ctx,
      attempt: reconnectAttempts,
      maxAttempts: MAX_RECONNECT_ATTEMPTS
    })
    screenLifecycleStats.recordWsReconnect({
      attempt: reconnectAttempts,
      maxAttempts: MAX_RECONNECT_ATTEMPTS,
    })
    syncNotifyWs(storeId)
  }, RECONNECT_INTERVAL_MS)
}

export function disconnectNotifyWs({ resetAttempts = true } = {}) {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (resetAttempts) reconnectAttempts = 0
  manualDisconnect = true
  screenLifecycleStats.recordWsDisconnect({ manual: true })
  try {
    if (socket) socket.close(1000, 'manual')
  } catch (_) {}
  socket = null
}
